import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Result, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import urls from "urls";
import { WAVE_DEFAULT_SIZE } from "constants/PickingConstant";
import { buildProductIndex } from "functions/pickLines";
import { buildPickRouteIndex } from "functions/pickRoute";
import { previewWaveOrders, selectWaveCandidates } from "functions/waveDocument";

const { Text } = Typography;

const WARNING_COLORS = { "off-route": "orange", "no-weight": "gold", "box-count-mismatch": "red", "no-vak-code": "orange", "loose-pieces": "gold" };

/**
 * Compose a pick round (HVC-48): which orders of the released day go on the next cart.
 *
 * Shows every candidate as the round would carry it - lines, box advice, what is off the route -
 * using the same builder the round is written with. Orders that cannot be mapped (a product that
 * no longer exists, a broken package) are listed and cannot be ticked: they need fixing, not
 * picking. Preselects the orders ticked in the table when there are any, otherwise the oldest.
 */
const WaveComposeModal = ({
  open,
  onClose,
  pickDay,
  deliveryDates,
  gapDays,
  day,
  dayOrders,
  selectedRefs,
  products,
  loadedShops,
  pickRouteEntries,
  links,
  createWave,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const productIndex = useMemo(() => buildProductIndex(products), [products]);
  const pickRouteIndex = useMemo(() => buildPickRouteIndex(pickRouteEntries), [pickRouteEntries]);
  const candidates = useMemo(() => selectWaveCandidates(dayOrders.orders), [dayOrders.orders]);
  const preview = useMemo(
    () => (open ? previewWaveOrders({ orders: candidates, productIndex, pickRouteIndex, links }) : []),
    [open, candidates, productIndex, pickRouteIndex, links],
  );
  const catalogueMissing = ["VCE", "HVO"].filter(
    (shop) => candidates.some((order) => order.shopSource === shop) && !(loadedShops || []).includes(shop),
  );

  useEffect(() => {
    if (!open) return;
    setResult(null);
    const mappable = preview.filter((row) => row.ok).map((row) => row.orderRef);
    const fromTable = mappable.filter((ref) => (selectedRefs || []).includes(ref));
    setChosen(fromTable.length > 0 ? fromTable : mappable.slice(0, WAVE_DEFAULT_SIZE));
    // Only when the modal opens: a background refresh must not undo the user's ticks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const orders = candidates.filter((order) => chosen.includes(`${order.shopSource}/${order.id}`));
      const outcome = await createWave({
        pickDay,
        gapDays,
        deliveryDates,
        pickDayDoc: day.doc,
        orders,
        productIndex,
        pickRouteIndex,
        links,
        loadedShops,
        nextWaveNumber: day.nextWaveNumber,
      });
      setResult(outcome);
    } catch (error) {
      setResult({ ok: false, reason: "wave/error", message: error.message });
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      title: t("orderpicking.compose.shop"),
      dataIndex: "shop",
      width: 70,
      render: (_, row) => <Tag color={row.order.shopSource === "VCE" ? "green" : "purple"}>{row.order.shopSource}</Tag>,
    },
    {
      title: t("orderpicking.compose.order"),
      dataIndex: "orderId",
      render: (_, row) => (
        <div>
          <strong>{row.order.orderId}</strong>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {row.order.delivery?.type === "pickup" ? t("orderpicking.compose.pickup") : t("orderpicking.compose.send")} ·{" "}
            {row.order.delivery?.date}
          </div>
        </div>
      ),
    },
    {
      title: t("orderpicking.compose.lines"),
      dataIndex: "lines",
      width: 80,
      render: (_, row) => (row.ok ? row.waveOrder.lines.length : "-"),
    },
    {
      title: t("orderpicking.compose.boxes"),
      dataIndex: "boxes",
      width: 150,
      render: (_, row) => (row.ok ? row.waveOrder.boxAdvice : "-"),
    },
    {
      title: t("orderpicking.compose.notes"),
      dataIndex: "notes",
      render: (_, row) =>
        row.ok ? (
          <Space size={4} wrap>
            {row.warnings.map((warning) => (
              <Tag key={warning.kind} color={WARNING_COLORS[warning.kind] || "default"}>
                {t(`orderpicking.compose.warning_${warning.kind.replace(/-/g, "_")}`, { count: warning.count })}
              </Tag>
            ))}
          </Space>
        ) : (
          <Tooltip title={(row.refused.problems || []).join(" ")}>
            <Tag color="red">{t("orderpicking.compose.refused")}</Tag>
            <Text type="danger" style={{ fontSize: 12 }}>
              {(row.refused.problems || [])[0]}
            </Text>
          </Tooltip>
        ),
    },
  ];

  const renderResult = () => {
    if (result.ok) {
      return (
        <Result
          status="success"
          title={t("orderpicking.compose.created", { number: result.wave.waveNumber, count: result.wave.orderCount })}
          subTitle={result.refused.length > 0 ? t("orderpicking.compose.created_refused", { count: result.refused.length }) : null}
          extra={[
            <Button type="primary" key="go" onClick={() => navigate(`${urls.pickWaves}?pickDay=${pickDay}&wave=${result.wave.waveId}`)}>
              {t("orderpicking.compose.to_rounds")}
            </Button>,
            <Button key="close" onClick={onClose}>
              {t("close")}
            </Button>,
          ]}
        />
      );
    }
    return (
      <Alert
        type="error"
        showIcon
        message={t(`orderpicking.compose.error_${(result.reason || "wave/error").replace("wave/", "").replace(/-/g, "_")}`, {
          shops: (result.shops || []).join(", "),
          orders: (result.orderIds || []).join(", "),
        })}
        description={result.message || (result.problems || []).join(" ")}
      />
    );
  };

  return (
    <Modal
      open={open}
      title={t("orderpicking.compose.title", { day: pickDay })}
      width={960}
      onCancel={onClose}
      footer={
        result && result.ok
          ? null
          : [
              <Text key="count" type="secondary" style={{ marginRight: 12 }}>
                {t("orderpicking.compose.chosen", { count: chosen.length, size: WAVE_DEFAULT_SIZE })}
              </Text>,
              <Button key="cancel" onClick={onClose}>
                {t("cancel")}
              </Button>,
              <Button
                key="create"
                type="primary"
                loading={busy}
                disabled={chosen.length === 0 || catalogueMissing.length > 0 || !day.locked}
                onClick={handleCreate}
              >
                {t("orderpicking.compose.create", { count: chosen.length })}
              </Button>,
            ]
      }
    >
      {!!result && renderResult()}
      {!(result && result.ok) && (
        <>
          <p style={{ color: "#6b6b6b" }}>{t("orderpicking.compose.help")}</p>
          {catalogueMissing.length > 0 && (
            <Alert type="error" showIcon className="mb-3" message={t("orderpicking.compose.error_catalogue_not_loaded", { shops: catalogueMissing.join(", ") })} />
          )}
          {chosen.length > WAVE_DEFAULT_SIZE && (
            <Alert type="info" showIcon className="mb-3" message={t("orderpicking.compose.over_size", { count: chosen.length, size: WAVE_DEFAULT_SIZE })} />
          )}
          <Table
            size="small"
            rowKey="orderRef"
            columns={columns}
            dataSource={preview}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
            rowSelection={{
              selectedRowKeys: chosen,
              onChange: setChosen,
              getCheckboxProps: (row) => ({ disabled: !row.ok }),
            }}
            locale={{ emptyText: t("orderpicking.compose.no_candidates") }}
          />
        </>
      )}
    </Modal>
  );
};

export default WaveComposeModal;
